import { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useConfigStore } from "../../../store/configStore";
import { isStaticSpineTemplateType } from "../../../types/spine-template";
import { useEditorStore } from "../../../store/editorStore";
import { Accordion } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CreateSelectionDialog } from "../../../components/CreateSelectionDialog";
import { SortableSelectionItem } from "./list-components/SortableSelectionItem";
import { StaticSortableSelectionItem } from "./list-components/StaticSortableSelectionItem";
import { RelatedSelectionItem } from "./list-components/RelatedSelectionItem";
import { DownloadRelatedTemplateButton } from "./list-components/DownloadRelatedTemplateButton";
import { Download } from "lucide-react";

export function SelectionList() {
  const {
    templateConfig,
    loadedRelatedTemplates,
    allRelatedTemplateIds,
    templateType,
  } = useConfigStore();
  const isStaticLayer = isStaticSpineTemplateType(templateType);
  const { updateSelectionOrder } = useEditorStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // 排序列表
  const mainSelections = useMemo(() => {
    if (!templateConfig?.selection) return [];
    return [...templateConfig.selection].sort(
      (a, b) => a.playIndex - b.playIndex,
    );
  }, [templateConfig]);

  // 已下载的关联模版分组
  const relatedGroups = useMemo(() => {
    if (!loadedRelatedTemplates) return [];
    return loadedRelatedTemplates.map((rel) => ({
      ...rel,
      selections: (rel.config.selection || []).map((sel: any) => ({
        ...sel,
        id: `${rel.id}_${sel.id}`, // 避免 ID 冲突
        originalId: sel.id,
        isRelated: true,
        relatedTemplateId: rel.id,
        // 传入关联模版的 skus 配置
        skusMap: rel.config.skus,
      })),
    }));
  }, [loadedRelatedTemplates]);

  // 已下载的关联模版 ID 集合
  const downloadedIds = useMemo(
    () => new Set(loadedRelatedTemplates?.map((r) => r.id) || []),
    [loadedRelatedTemplates],
  );

  // 未下载的关联模版 ID 列表
  const pendingIds = useMemo(
    () => (allRelatedTemplateIds || []).filter((id) => !downloadedIds.has(id)),
    [allRelatedTemplateIds, downloadedIds],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = mainSelections.findIndex((s) => s.id === active.id);
      const newIndex = mainSelections.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(mainSelections, oldIndex, newIndex);
        updateSelectionOrder(newOrder);
      }
    }
  };

  if (!templateConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm gap-2">
        <p>暂无数据</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* 工具栏 */}
      <div className="flex items-center justify-between mb-3 pb-3 border-b">
        <CreateSelectionDialog />
        <span className="text-xs text-muted-foreground">
          主模版: {mainSelections.length} / 关联:{" "}
          {relatedGroups.reduce((acc, g) => acc + g.selections.length, 0)}
        </span>
      </div>

      {/* 列表 */}
      <ScrollArea className="flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* 主模版列表（可排序） */}
          <div className="mb-4">
            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-2">
              <span>主模版组件</span>
              <Badge variant="outline" className="text-[10px] px-1 h-4">
                {mainSelections.length}
              </Badge>
            </div>
            <SortableContext
              items={mainSelections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <Accordion type="single" collapsible>
                {mainSelections.map((selection) =>
                  isStaticLayer ? (
                    <StaticSortableSelectionItem
                      key={selection.id}
                      selection={selection}
                    />
                  ) : (
                    <SortableSelectionItem
                      key={selection.id}
                      selection={selection}
                    />
                  ),
                )}
              </Accordion>
            </SortableContext>
          </div>

          {/* 已下载的关联模版列表（分组展示，不可排序） */}
          {relatedGroups.map((group) => (
            <div key={group.id} className="mb-4">
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/20 rounded-sm mb-1 mx-1 flex items-center justify-between">
                <span>关联: {group.id}</span>
                <Badge variant="secondary" className="text-[10px] px-1 h-4">
                  {group.selections.length}
                </Badge>
              </div>
              <Accordion type="single" collapsible>
                {group.selections.map((selection: any) => (
                  <RelatedSelectionItem
                    key={selection.id}
                    selection={selection}
                    spineData={group.spineData}
                  />
                ))}
              </Accordion>
            </div>
          ))}

          {/* 未下载的关联模版（显示下载按钮） */}
          {pendingIds.map((id) => (
            <div key={id} className="mb-4">
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/20 border border-dashed border-muted-foreground/30 rounded-lg mx-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Download className="h-3.5 w-3.5 text-muted-foreground/50" />
                  <span>关联: {id}</span>
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1 h-4 text-muted-foreground/60"
                  >
                    未加载
                  </Badge>
                </div>
                <div>
                  <DownloadRelatedTemplateButton templateId={id} />
                </div>
              </div>
            </div>
          ))}
        </DndContext>
      </ScrollArea>
    </div>
  );
}
